import { useState, useEffect, useRef, useContext } from 'react';
import { formatDirection, formatShipmentType } from '../../utils/shipmentHelpers';
import { 
  CircularProgress, 
  Tabs, 
  Tab, 
  Button, 
  TextField, 
  Avatar, 
  Badge, 
  IconButton,
  Divider,
  Paper,
  InputAdornment,
  Typography,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import { 
  Search, 
  Send, 
  MoreVert, 
  Phone, 
  AttachFile, 
  FilterList,
  CheckCircle,
  AccessTime,
  ExitToApp,
  Error as ErrorIcon,
  Refresh
} from '@mui/icons-material';
import Select from 'react-select';
import { UserContext } from '../../App';
import { getAuthToken } from '../../utils/cookieUtils';
import { 
  saveFacilityToStorage, 
  loadFacilityFromStorage, 
  formatFacilityOptions
} from '../../utils/facilityStorage';

console.log('DriverComms.tsx file loaded');
console.log('Current URL:', window.location.href);
console.log('Current pathname:', window.location.pathname);

// Environment variables - replace REST API URLs with GraphQL URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';
const REFRESH_INTERVAL = 7500; // 5 seconds refresh interval

// Types
interface Facility {
  id: number;
  name: string;
}

interface ActiveChat {
  shipment_id: string;
  shipment_number: string;
  shipment_type: string;
  direction: string;
  driver_name: string;
  driver_phone: string;
  truck_number: string;
  trailer_number: string;
  last_activity: string;
  last_message: string;
  message_count: number;
  dock_assignment: string;
  threadUuid: string;
  participantUuid?: string; // UUID of the participant to send message to
  // Add new fields for enhanced display
  carrier_name?: string;
  checkin_time?: string;
  current_location?: string;
}

interface DriverWithoutChat {
  driver_id: string;
  shipment_type: string;
  direction: string;
  driver_name: string;
  driver_phone: string;
  shipment_id: string;
  shipment_number: string;
  truck_number: string;
  trailer_number: string;
  display_text: string;
  dock_assignment: string;
  threadUuid?: string; // Optional for drivers without chat
  participantUuid?: string; // UUID of the participant to send message to
}

interface ChatHistoryMessage {
  message: string;
  timestamp: string;
  sender: 'YARD' | 'DRIVER';
  sender_name?: string; // Sender name for YARD messages
}

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderRole: 'driver' | 'staff';
  content: string;
  timestamp: Date;
  isRead: boolean;
}

// GraphQL Types
interface ThreadResponse {
  uuid: string;
  shipmentId: string;
  facilityId: string;
  message: string;
  createdAt: string;
  fromParticipant: {
    uuid: string;
    participantType: {
      type: string;
    };
    driver?: {
      first_name: string;
      last_name?: string;
    };
  };
  toParticipant: {
    participantType: {
      type: string;
    };
    uuid: string;
  };
  shipment?: {
    reference_number: string;
    direction: string;
    metadata: {
      shipment_type: string;
    };
    driver_assignment?: {
      driver: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
      };
      createdAt: string;
    };
    trailer?: {
      trailerNumber: string;
      carrier: {
        name: string;
      };
      current_location?: {
        __typename: 'YardSpotObject' | 'FacilityZoneObject' | 'DockObject';
        uuid: string;
        name: string;
      };
    };
  };
}

interface MessageResponse {
  uuid: string;
  message: string;
  createdAt: string;
  fromParticipant: {
    uuid: string;
    name?: string;
    participantType: {
      type: string;
    };
    driver: {
      first_name: string;
    }
  };
  toParticipant: {
    uuid: string;
    participantType: {
      type: string;
    };
  };
}

type DriverStatus = 'pre-checkin' | 'checkin' | 'checkout';

// Helper functions
const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

const formatDate = (date: Date): string => {
  const today = new Date();
  
  if (date.toDateString() === today.toDateString()) {
    return formatTime(date);
  }
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday ' + formatTime(date);
  }
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  }) + ' ' + formatTime(date);
};

const getInitials = (name: string | null | undefined): string => {
  if (!name) return ''; // Return empty string if name is null or undefined
  
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase();
};

function DriverComms() {
  // Add UserContext and extract thread ID from URL directly
  // @ts-ignore
  const { user } = useContext(UserContext);
  
  // Extract thread ID from URL path directly
  const getThreadIdFromUrl = () => {
    const pathname = window.location.pathname;
    const match = pathname.match(/\/drivers\/comms\/([a-f0-9-]+)/);
    return match ? match[1] : null;
  };
  
  const threadId = getThreadIdFromUrl();
  
  // Debug: Log threadId immediately
  console.log('DriverComms component loaded, threadId from URL:', threadId);
  
  // State for facilities
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacilityState] = useState<Facility | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });
  const [sinceHours, setSinceHours] = useState<number>(() => {
    // If there's a threadId in the URL, use 1 week to ensure we find the thread
    return threadId ? 168 : 24; // Default to 24 hours, or 1 week if deep linking
  });

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacility = (facility: Facility | null) => {
    setSelectedFacilityState(facility);
    saveFacilityToStorage(facility);
  };
  
  // State for drivers and chats
  const [activeChats, setActiveChats] = useState<ActiveChat[]>([]);
  const [driversWithoutChats, setDriversWithoutChats] = useState<DriverWithoutChat[]>([]);
  const [filteredDriversList, setFilteredDriversList] = useState<(ActiveChat | DriverWithoutChat)[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<ActiveChat | DriverWithoutChat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [unreadThreadUuids, setUnreadThreadUuids] = useState<Set<string>>(new Set());
  const [newMessage, setNewMessage] = useState<string>('');
  
  // UI state
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMessages, setLoadingMessages] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tabValue, setTabValue] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Add refs for interval management
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Function to fetch drivers and chats
  const fetchDriversAndChats = async (showLoadingIndicator = true) => {
    if (!selectedFacility) return Promise.resolve();
    
    if (showLoadingIndicator) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    
    setError(null);
    
    try {
      // Combined GraphQL query for fetching active chats, unassigned drivers, and unread threads
      const combinedQuery = `
      query {
        recentThreadsByFacilityId(facilityId: "${selectedFacility.id}", sinceHours: ${sinceHours}) {
          threads {
            uuid
            shipmentId
            facilityId
            message
            createdAt
            fromParticipant {
              participantType { type }
              uuid
              driver {
                first_name
              }
            }
            toParticipant {
              participantType { type }
              uuid
            }
            shipment {
              reference_number
              metadata
              direction
              driver_assignment {
                 driver{
                firstName
                lastName
                phoneNumber
                } 
                createdAt
              }
                 trailer {
                  trailerNumber,
                  carrier {
                    name
                  }
                  current_location {
              ... on YardSpotObject {
                __typename
                uuid
                name
              }
              ... on FacilityZoneObject {
                __typename
                uuid
                name
              }
              ... on DockObject {
                __typename
                uuid
                name
              }
            }
                }
                
            }
          }
        }
        unreadThreadsByFacilityId(facilityId: "${selectedFacility.id}", sinceHours: ${sinceHours}) {
          threads {
            uuid
          }
        }
        unassignedDriverAssignments(facilityId: "${selectedFacility.id}", sinceHours: ${sinceHours}) {
          success
          message
          totalCount
          driverAssignments {
            id
            shipmentId
            userId
            driverId
            phone
            assignedAt
            revokedAt
            checkIn
            checkOut
            status
            archived
            archivedAt
            createdAt
            updatedAt
            
            driver {
              id
              firstName
              lastName
            }
            
            shipment {
              id
              referenceNumber
              metadata
              direction
            }
          }
        }
      }
    `;

      // Get auth token from cookie or user context
      const authToken = getAuthToken() || user?.token;

      // Fetch both active chats and unassigned drivers using combined GraphQL query
      const combinedResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: combinedQuery
        })
      });

      const combinedResult = await combinedResponse.json();
      
      if (combinedResult.errors) {
        throw new Error(combinedResult.errors[0].message);
      }

      // Extract unread thread UUIDs
      const unreadUuids = new Set<string>(
        combinedResult.data.unreadThreadsByFacilityId?.threads?.map((t: any) => t.uuid as string) || []
      );
      setUnreadThreadUuids(unreadUuids);

      // Transform GraphQL response to match the expected format
        // This will be a simplified version - we need to fetch additional driver info
        const transformedActiveChats = combinedResult.data.recentThreadsByFacilityId.threads.map((thread: ThreadResponse) => {
          // Determine which participant UUID to use for replies
          // If the thread is from a driver to facility, we want to reply to the driver (fromParticipant)
          // If the thread is from facility to driver, we reply to the driver (toParticipant)
          console.log(thread);
          const participantUuid = thread.fromParticipant.participantType.type === 'driver' 
            ? thread.fromParticipant.uuid 
            : thread.toParticipant.uuid;
          
          // Get driver details from shipment.driver_assignment
          const driverAssignment = thread.shipment?.driver_assignment;
          const driver = driverAssignment?.driver;
          const driverName = driver 
            ? `${driver.firstName || ''} ${driver.lastName || ''}`.trim()
            : (thread.fromParticipant.participantType.type === 'driver' && thread.fromParticipant.driver
                ? `${thread.fromParticipant.driver.first_name || ''} ${thread.fromParticipant.driver.last_name || ''}`.trim()
                : "Driver");
          
          // Get trailer info
          const trailer = thread.shipment?.trailer;
          const trailerNumber = trailer?.trailerNumber || '';
          const carrierName = trailer?.carrier?.name || '';
          
          // Get current location
          let currentLocation = 'In Facility';
          if (trailer?.current_location) {
            const locationType = trailer.current_location.__typename === 'YardSpotObject' ? 'YardSpot' :
                                 trailer.current_location.__typename === 'DockObject' ? 'DockDoor' :
                                 trailer.current_location.__typename === 'FacilityZoneObject' ? 'FacilityZone' : '';
            currentLocation = `${locationType}: ${trailer.current_location.name}`;
          }
          
          return {
            shipment_id: thread.shipmentId,
            shipment_type: formatShipmentType(thread.shipment?.metadata?.shipment_type) || '',
            shipment_number: thread.shipment?.reference_number || thread.shipmentId,
            direction: formatDirection(thread.shipment?.direction || ''),
            driver_name: driverName,
            driver_phone: driver?.phoneNumber || '',
            truck_number: "", // Not available in current schema
            trailer_number: trailerNumber,
            last_activity: thread.createdAt,
            last_message: thread.message,
            message_count: 1, // Placeholder for unread count
            dock_assignment: "",
            threadUuid: thread.uuid,
            participantUuid: participantUuid,
            // Add new fields for enhanced display
            carrier_name: carrierName,
            checkin_time: driverAssignment?.createdAt || null,
            current_location: currentLocation
          };
        });

      setActiveChats(transformedActiveChats);
      
      // Process unassigned drivers from the combined result
      let driversWithoutChats: DriverWithoutChat[] = [];
      
      if (combinedResult.data.unassignedDriverAssignments?.driverAssignments) {
        // Transform unassigned drivers to match DriverWithoutChat interface
        const transformedUnassignedDrivers = combinedResult.data.unassignedDriverAssignments.driverAssignments.map((assignment: any) => {
          const driverName = assignment.driver 
            ? `${assignment.driver.firstName || ''} ${assignment.driver.lastName || ''}`.trim()
            : 'Unknown Driver';
          
          return {
            driver_id: assignment.driverId || assignment.id,
            driver_name: driverName,
            driver_phone: assignment.phone || '',
            shipment_id: assignment.shipmentId,
            shipment_number: assignment.shipment?.referenceNumber || assignment.shipmentId,
            shipment_type: formatShipmentType(assignment.shipment?.metadata?.shipment_type),
            direction: formatDirection(assignment.shipment?.direction || ''),
            truck_number: '',  // Not available in current schema
            trailer_number: '', // Not available in current schema
            display_text: `${driverName} - ${assignment.shipment?.referenceNumber || assignment.shipmentId}`,
            dock_assignment: '', // Not available for unassigned drivers
            threadUuid: undefined, // No existing thread for available drivers
            participantUuid: undefined // Will be created when first message is sent
          };
        });

        // Filter out drivers that already have active chats to avoid duplicates
        const activeShipmentIds = transformedActiveChats.map((chat: ActiveChat) => chat.shipment_id);
        driversWithoutChats = transformedUnassignedDrivers.filter(
          (driver: DriverWithoutChat) => !activeShipmentIds.includes(driver.shipment_id)
        );
      } else {
        console.warn('Failed to fetch unassigned drivers or no data available');
      }
      
      setDriversWithoutChats(driversWithoutChats);
      
      // Combine and filter based on current tab and search
      const combined = [...transformedActiveChats, ...driversWithoutChats];
      setFilteredDriversList(filterDriversList(combined, tabValue, searchTerm));
      
      console.log('Data refreshed successfully via GraphQL');
    } catch (err) {
      console.error('Failed to fetch drivers and chats:', err);
      setError('Failed to fetch drivers and chats');
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      } else {
        // Small delay to ensure the refreshing indicator is visible
        setTimeout(() => setIsRefreshing(false), REFRESH_INTERVAL);
      }
    }
    
    return Promise.resolve();
  };
  
  // Function to fetch messages for the selected driver
  const fetchMessages = async (driver: ActiveChat | DriverWithoutChat, showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setLoadingMessages(true);
    }
    
    try {
      // If there's no threadUuid, this is a new conversation
      if (!driver.threadUuid) {
        setMessages([{
          id: 'msg-system-new',
          senderId: 'system',
          senderName: 'System',
          senderRole: 'staff',
          content: `Start a new conversation with ${driver.driver_name}`,
          timestamp: new Date(),
          isRead: true
        }]);
        return Promise.resolve();
      }
      
      // GraphQL query for chat messages
      const messagesQuery = `
        query {
          thread(thread_uuid: "${driver.threadUuid}") {
            messages {
              uuid
              message
              createdAt
              fromParticipant {
                uuid
                driver {
                  first_name
                }
                facility {
                  name
                }
                participantType { type }
              }
              toParticipant {
                uuid
                driver {
                  first_name
                }
                facility {
                  name
                }
                participantType { type }
              }
            }
          }
        }
      `;

      // Get auth token from cookie or user context
      const authToken = getAuthToken() || user?.token;

      // Fetch messages using GraphQL
      const messagesResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: messagesQuery
        })
      });

      const messagesResult = await messagesResponse.json();
      
      if (messagesResult.errors) {
        throw new Error(messagesResult.errors[0].message);
      }

      if (messagesResult.data.thread && 
          messagesResult.data.thread.messages && 
          Array.isArray(messagesResult.data.thread.messages)) {
        
        // Convert the GraphQL response to our Message format
        const chatMessages: Message[] = messagesResult.data.thread.messages.map((msg: MessageResponse, index: number) => ({
          id: msg.uuid || `msg-${index}-${new Date(msg.createdAt).getTime()}`,
          senderId: msg.fromParticipant.uuid,
          senderName: msg.fromParticipant.name || (msg.fromParticipant.participantType.type === 'driver' ? driver.driver_name : 'Facility'),
          senderRole: msg.fromParticipant.participantType.type === 'driver' ? 'driver' : 'staff',
          content: msg.message,
          timestamp: new Date(msg.createdAt),
          isRead: true
        }));
        
        setMessages(chatMessages);
        
        // If there are no messages yet, show a welcome message for new chats
        if (chatMessages.length === 0) {
          setMessages([{
            id: 'msg-system-new',
            senderId: 'system',
            senderName: 'System',
            senderRole: 'staff',
            content: `Start a new conversation with ${driver.driver_name}`,
            timestamp: new Date(),
            isRead: true
          }]);
        }
      } else {
        // Fallback in case the API doesn't return expected format
        setMessages([{
          id: 'msg-system-new',
          senderId: 'system',
          senderName: 'System',
          senderRole: 'staff',
          content: `Start a new conversation with ${driver.driver_name}`,
          timestamp: new Date(),
          isRead: true
        }]);
      }
      
      console.log('Messages refreshed successfully via GraphQL');
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      // Show a fallback message only if this is the initial load
      if (showLoadingIndicator) {
        setMessages([{
          id: 'msg-system-error',
          senderId: 'system',
          senderName: 'System',
          senderRole: 'staff',
          content: 'Could not load chat history. Please try again later.',
          timestamp: new Date(),
          isRead: true
        }]);
      }
    } finally {
      if (showLoadingIndicator) {
        setLoadingMessages(false);
      }
    }
    
    return Promise.resolve();
  };
  
  // Fetch facilities
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        // GraphQL query for facilities
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        // Get auth token from cookie or user context
        const authToken = getAuthToken() || user?.token;

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, set default
            if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
          }
        }
      } catch (err) {
        setError('Failed to fetch facilities');
        console.error(err);
      }
    };
    
    fetchFacilities();
  }, [user]);
  
  // Fetch drivers and chats when facility or sinceHours changes
  useEffect(() => {
    if (!selectedFacility) return;
    
    fetchDriversAndChats(true);
  }, [selectedFacility, searchTerm, sinceHours]);
  
  // Auto-refresh data at regular intervals
  useEffect(() => {
    if (!selectedFacility) return;
    
    console.log('Setting up auto-refresh interval');
    
    // Clear existing interval if any
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Set up interval for refreshing data
    intervalRef.current = setInterval(() => {
      console.log('Auto-refreshing data...');
      // Refresh drivers and chats data without showing loading indicator
      fetchDriversAndChats(false);
      
      // Refresh messages for selected driver without showing loading indicator
      if (selectedDriver) {
        fetchMessages(selectedDriver, false);
      }
    }, REFRESH_INTERVAL);
    
    // Clean up interval on unmount
    return () => {
      console.log('Cleaning up auto-refresh interval');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [selectedFacility, selectedDriver, sinceHours]); // Include sinceHours so auto-refresh uses updated value
  
  // Filter drivers based on tab and search term
  useEffect(() => {
    if (activeChats.length === 0 && driversWithoutChats.length === 0) return;
    
    const combined = [...activeChats, ...driversWithoutChats];
    setFilteredDriversList(filterDriversList(combined, tabValue, searchTerm));
  }, [tabValue, searchTerm, activeChats, driversWithoutChats]);
  
  // Fetch messages when driver is selected
  useEffect(() => {
    if (!selectedDriver) {
      setMessages([]);
      return;
    }
    
    fetchMessages(selectedDriver, true);
    scrollToBottom();
  }, [selectedDriver]);

  // Handle deep linking from URL parameters
  useEffect(() => {
    if (threadId && (activeChats.length > 0 || driversWithoutChats.length > 0)) {
      // Search in both activeChats and driversWithoutChats for the matching threadUuid
      const allDrivers = [...activeChats, ...driversWithoutChats];
      const targetDriver = allDrivers.find(driver => driver.threadUuid === threadId);
      
      if (targetDriver && (!selectedDriver || selectedDriver.threadUuid !== targetDriver.threadUuid)) {
        console.log('✅ Deep linking to thread found:', threadId);
        setSelectedDriver(targetDriver);
        
        // Mark thread as read when deep linking
        if (targetDriver.threadUuid && selectedFacility) {
          markThreadAsRead(targetDriver.threadUuid, selectedFacility.id.toString())
            .then(() => {
              console.log('Deep linked thread marked as read');
              // Remove from unread set
              setUnreadThreadUuids(prev => {
                const newSet = new Set(prev);
                newSet.delete(targetDriver.threadUuid!);
                return newSet;
              });
            })
            .catch(err => console.error('Failed to mark deep linked thread as read:', err));
        }
      } else if (!targetDriver) {
        // Thread not found in current data - fetch it specifically
        console.log('🔍 Thread not found, fetching:', threadId);
        fetchSpecificThread(threadId);
      }
    }
  }, [threadId, activeChats, driversWithoutChats, selectedDriver]);

  // Function to fetch a specific thread when deep linking
  const fetchSpecificThread = async (specificThreadId: string) => {
    if (!selectedFacility) return;
    
    try {
      const authToken = getAuthToken() || user?.token;

      // Query to get a specific thread by UUID - using the same structure as the messages query
      const specificThreadQuery = `
        query {
          thread(thread_uuid: "${specificThreadId}") {
            messages {
              uuid
              message
              createdAt
              fromParticipant {
                uuid
                participantType { type }
                driver {
                  firstName
                  lastName
                }
              }
              toParticipant {
                uuid
                participantType { type }
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: specificThreadQuery
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('Failed to fetch specific thread:', result.errors[0].message);
        return;
      }

      const thread = result.data.thread;
      if (thread && thread.messages && thread.messages.length > 0) {
        // Get the most recent message to build the ActiveChat
        const latestMessage = thread.messages[0]; // Assuming messages are ordered by newest first
        
        // Determine which participant is the driver
        const isFromDriver = latestMessage.fromParticipant.participantType.type === 'driver';
        const driverParticipant = isFromDriver ? latestMessage.fromParticipant : latestMessage.toParticipant;
        const driver = driverParticipant.driver;
        
        const driverName = driver 
          ? `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || "Unknown Driver"
          : "Unknown Driver";
        
        const newActiveChat: ActiveChat = {
          shipment_id: driverParticipant.uuid, // Using participant UUID as fallback
          shipment_number: `Thread ${specificThreadId.substring(0, 8)}`, // Shortened thread UUID
          shipment_type: formatShipmentType(thread.shipment?.metadata?.shipment_type),
          driver_name: driverName,
          driver_phone: '', // Not available in this query
          direction: formatDirection(thread.shipment?.direction || ''),
          truck_number: "",
          trailer_number: '',
          last_activity: latestMessage.createdAt,
          last_message: latestMessage.message,
          message_count: thread.messages.length,
          dock_assignment: "",
          threadUuid: specificThreadId, // Use the thread UUID we're looking for
          participantUuid: driverParticipant.uuid,
          carrier_name: '',
          checkin_time: undefined,
          current_location: ''
        };

        // Add to activeChats and select it
        setActiveChats(prev => [newActiveChat, ...prev]);
        setSelectedDriver(newActiveChat);
        
        // Mark the fetched thread as read
        if (newActiveChat.threadUuid && selectedFacility) {
          markThreadAsRead(newActiveChat.threadUuid, selectedFacility.id.toString())
            .then(() => {
              console.log('Fetched thread marked as read');
              // Remove from unread set
              setUnreadThreadUuids(prev => {
                const newSet = new Set(prev);
                newSet.delete(newActiveChat.threadUuid!);
                return newSet;
              });
            })
            .catch(err => console.error('Failed to mark fetched thread as read:', err));
        }
        
        console.log('✅ Fetched and selected specific thread:', specificThreadId);
      }
    } catch (err) {
      console.error('Error fetching specific thread:', err);
    }
  };
  
  // Scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Helper functions
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const filterDriversList = (
    driversList: (ActiveChat | DriverWithoutChat)[], 
    tab: number, 
    search: string
  ) => {
    let filtered = [...driversList];
    
    // Apply tab filter
    // 0: All, 1: Active Chats, 2: Available Drivers
    if (tab === 1) {
      filtered = filtered.filter(driver => 'last_message' in driver);
    } else if (tab === 2) {
      filtered = filtered.filter(driver => !('last_message' in driver));
    }
    
    // Apply search filter for the UI filtering (server filtering is already done for drivers without chats)
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(driver => {
        // Common properties
        const nameMatch = driver.driver_name.toLowerCase().includes(searchLower);
        const shipmentMatch = driver.shipment_number.toLowerCase().includes(searchLower);
        const truckMatch = driver.truck_number?.toLowerCase()?.includes(searchLower) || false;
        const trailerMatch = driver.trailer_number?.toLowerCase()?.includes(searchLower) || false;
        const carrierMatch = ('carrier_name' in driver && driver.carrier_name) ? 
          driver.carrier_name.toLowerCase().includes(searchLower) : false;
        const phoneMatch = driver.driver_phone?.toLowerCase()?.includes(searchLower) || false;
        const locationMatch = ('current_location' in driver && driver.current_location) ? 
          driver.current_location.toLowerCase().includes(searchLower) : false;
        
        return nameMatch || shipmentMatch || truckMatch || trailerMatch || carrierMatch || phoneMatch || locationMatch;
      });
    }
    
    return filtered;
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  const markThreadAsRead = async (threadUuid: string, facilityId: string) => {
    try {
      const markAsReadMutation = `
        mutation MarkThreadAsRead($threadUuid: String!, $facilityId: String!) {
          markThreadAsRead(
            threadUuid: $threadUuid
            facilityId: $facilityId
          ) {
            success
          }
        }
      `;

      // Get auth token from cookie or user context
      const authToken = getAuthToken() || user?.token;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: markAsReadMutation,
          variables: {
            threadUuid,
            facilityId
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('Failed to mark thread as read:', result.errors[0].message);
        return false;
      }

      return result.data?.markThreadAsRead?.success || false;
    } catch (err) {
      console.error('Error marking thread as read:', err);
      return false;
    }
  };

  const handleDriverSelect = async (driver: ActiveChat | DriverWithoutChat) => {
    setSelectedDriver(driver);
    
    // Mark thread as read if it has a threadUuid and we have a selectedFacility
    if (driver.threadUuid && selectedFacility) {
      try {
        await markThreadAsRead(driver.threadUuid, selectedFacility.id.toString());
        console.log('Thread marked as read successfully');
        
        // Remove from unread set immediately for UI update
        setUnreadThreadUuids(prev => {
          const newSet = new Set(prev);
          newSet.delete(driver.threadUuid!);
          return newSet;
        });
      } catch (err) {
        console.error('Failed to mark thread as read:', err);
        // Don't prevent the thread from opening if marking as read fails
      }
    }
  };
  
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedDriver) return;
    
    try {
      // Add message to UI immediately for better UX
      const newMsg: Message = {
        id: `msg-new-${Date.now()}`,
        senderId: 'staff-1',
        senderName: 'Facility',
        senderRole: 'staff',
        content: newMessage,
        timestamp: new Date(),
        isRead: true
      };
      
      setMessages([...messages, newMsg]);
      const messageContent = newMessage; // Store the message content before clearing
      setNewMessage('');
      
      // Check if this is a new conversation (no threadUuid)
      const isNewConversation = !selectedDriver.threadUuid;
      
      // Get auth token from cookie or user context
      const authToken = getAuthToken() || user?.token;

      let participantUuid = selectedDriver.participantUuid;
      
              // For new conversations, we need to get the driver's participant UUID first
        if (isNewConversation && !participantUuid) {
          // Only DriverWithoutChat has driver_id, ActiveChat uses a different approach
          const driverId = (selectedDriver as DriverWithoutChat).driver_id;
          
          if (!driverId) {
            throw new Error('Driver ID not available for new conversation');
          }

          const getParticipantQuery = `
            query GetMessageParticipant($driverId: ID!) {
              messageParticipantByDriverId(driverId: $driverId) {
                uuid
                driverId
                messageParticipantTypeUuid
                participantType {
                  uuid
                  type
                  description
                }
                driver {
                  id
                  first_name
                  last_name
                  phone_number
                  email
                }
                createdAt
                updatedAt
              }
            }
          `;

          const participantResponse = await fetch(GRAPHQL_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              query: getParticipantQuery,
              variables: {
                driverId: driverId
              }
            })
          });

          const participantResult = await participantResponse.json();
          
          if (participantResult.errors) {
            throw new Error(`Failed to get driver participant: ${participantResult.errors[0].message}`);
          }

          if (!participantResult.data?.messageParticipantByDriverId?.uuid) {
            throw new Error('Driver participant UUID not found');
          }

          participantUuid = participantResult.data.messageParticipantByDriverId.uuid;
          
          // Update the selected driver with the participant UUID for future messages
          const updatedDriver = {
            ...selectedDriver,
            participantUuid
          };
          setSelectedDriver(updatedDriver);
        }
      
      let sendMessageMutation;
      
      if (isNewConversation) {
        // Create new thread and send message for drivers without existing chats
        sendMessageMutation = `
          mutation {
            send_message(
              message_content: "${messageContent.replace(/"/g, '\\"')}"
              facility_id: "${selectedFacility?.id}"
              shipment_id: "${selectedDriver.shipment_id}"
              to_participant_uuid: "${participantUuid}"
              message_transport: "sms"
            ) {
              thread {
                uuid
              }
              message {
                uuid
                message
                created_at
              }
            }
          }
        `;
      } else {
        // Send message to existing thread
        sendMessageMutation = `
          mutation {
            send_message(
              message_content: "${messageContent.replace(/"/g, '\\"')}"
              thread_uuid: "${selectedDriver.threadUuid}"
              facility_id: "${selectedFacility?.id}"
              to_participant_uuid: "${participantUuid}"
              message_transport: "sms"
            ) {
              thread {
                uuid
              }
              message {
                uuid
                message
                created_at
              }
            }
          }
        `;
      }

      // Send message using GraphQL mutation
      const sendResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: sendMessageMutation
        })
      });

      const sendResult = await sendResponse.json();
      
      if (sendResult.errors) {
        throw new Error(sendResult.errors[0].message);
      }

      // Check if the message was sent successfully - the API returns the thread and message objects
      if (!sendResult.data.send_message || !sendResult.data.send_message.message) {
        throw new Error('Failed to send message');
      }
      
      // If this was a new conversation, update the selected driver with the new thread UUID
      if (isNewConversation && sendResult.data.send_message.thread?.uuid) {
        const updatedDriver = {
          ...selectedDriver,
          threadUuid: sendResult.data.send_message.thread.uuid,
          participantUuid // Make sure we keep the participant UUID we just retrieved
        };
        setSelectedDriver(updatedDriver);
      }
      
      // Refresh active chats to show updated message count
      if (selectedFacility) {
        // Refresh data without showing the loading indicator
        fetchDriversAndChats(false);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      // Show error in the UI
      setMessages([...messages, {
        id: `msg-error-${Date.now()}`,
        senderId: 'system',
        senderName: 'System',
        senderRole: 'staff',
        content: 'Failed to send message. Please try again.',
        timestamp: new Date(),
        isRead: true
      }]);
    }
  };
  
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const getDriverStatus = (driver: ActiveChat | DriverWithoutChat): DriverStatus => {
    // All drivers that show up in either list are considered checked in
    return 'checkin';
  };
  
  const getStatusColor = (status: DriverStatus): string => {
    switch (status) {
      case 'pre-checkin':
        return 'bg-yellow-500';
      case 'checkin':
        return 'bg-green-500';
      case 'checkout':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };
  
  const getStatusIcon = (status: DriverStatus) => {
    switch (status) {
      case 'pre-checkin':
        return <AccessTime className="text-yellow-500" />;
      case 'checkin':
        return <CheckCircle className="text-green-500" />;
      case 'checkout':
        return <ExitToApp className="text-gray-500" />;
      default:
        return null;
    }
  };
  
  const canSendMessage = (driver: ActiveChat | DriverWithoutChat | null) => {
    if (!driver) return false;
    return getDriverStatus(driver) !== 'checkout';
  };
  
  // Add a manual refresh handler
  const handleManualRefresh = () => {
    console.log('Manual refresh triggered');
    
    // Show the refreshing indicator
    setIsRefreshing(true);
    
    // Refresh drivers and chats
    fetchDriversAndChats(false)
      .then(() => {
        // Refresh messages if a driver is selected
        if (selectedDriver) {
          return fetchMessages(selectedDriver, false);
        }
      })
      .finally(() => {
        // Ensure the refreshing indicator is hidden
        setTimeout(() => setIsRefreshing(false), REFRESH_INTERVAL);
      });
  };
  
  if (error) {
    return (
      <div className="p-5 flex flex-col items-center justify-center h-full">
        <ErrorIcon color="error" fontSize="large" />
        <Typography variant="h6" color="error" className="mt-2">
          {error}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          className="mt-4"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="p-5 h-full">
      <div className="shadow bg-white p-5 mb-5 rounded">
        <div className="flex flex-col md:flex-row gap-3 mb-3">
          <div>
            <h1 className="text-2xl font-bold mb-2">Driver Communications</h1>
            <p className="text-gray-600">Communicate with drivers before, during, and after facility visits</p>
           
          </div>
          <div className="flex gap-3 ml-auto items-center">
            <div className="flex items-center">
              {isRefreshing ? (
                <div className="flex items-center">
                  <CircularProgress size={16} className="mr-2" />
                  <Typography variant="caption" color="textSecondary">
                    Refreshing...
                  </Typography>
                </div>
              ) : (
                <IconButton 
                  size="small" 
                  onClick={handleManualRefresh} 
                  title="Refresh"
                  className="mr-2"
                >
                  <Refresh fontSize="small" />
                </IconButton>
              )}
            </div>
            <Select
              options={formatFacilityOptions(facilities)}
              isSearchable={true}
              isClearable={false}
              value={selectedFacility ? {
                value: selectedFacility.id,
                label: selectedFacility.name,
                facility: selectedFacility
              } : null}
              placeholder="Select Facility"
              onChange={(selectedOption: any) => {
                setSelectedFacility(selectedOption?.facility || null);
              }}
              className="w-60"
            />
            <Select
              options={[
                { value: 24, label: '24 Hours' },
                { value: 48, label: '48 Hours' },
                { value: 168, label: 'Max (1 Week)' }
              ]}
              isSearchable={false}
              isClearable={false}
              value={{ value: sinceHours, label: sinceHours === 24 ? '24 Hours' : sinceHours === 48 ? '48 Hours' : 'Max (1 Week)' }}
              placeholder="Time Range"
              onChange={(selectedOption: any) => {
                setSinceHours(selectedOption?.value || 24);
              }}
              className="w-40"
            />
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5 h-[calc(100vh-220px)]">
        {/* Drivers List Panel */}
        <div className="md:col-span-1 bg-white shadow rounded overflow-hidden flex flex-col">
          <div className="p-3 border-b">
            <div className="mb-3">
              <TextField
                fullWidth
                placeholder="Search drivers, shipments..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="All" />
              <Tab label="Active Chats" />
              <Tab label="Available" />
            </Tabs>
          </div>
          
          <div className="overflow-y-auto flex-grow">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <CircularProgress />
              </div>
            ) : filteredDriversList.length === 0 ? (
              <div className="text-center p-5 text-gray-500">
                No drivers match your filters
              </div>
            ) : (
              <div>
                {filteredDriversList.map((driver, index) => {
                  const isActiveChat = 'last_message' in driver;
                  const driverStatus = getDriverStatus(driver);
                  const hasUnread = driver.threadUuid && unreadThreadUuids.has(driver.threadUuid);
                  const unreadCount = hasUnread ? 1 : 0;
                  
                  return (
                    <div 
                      key={`${driver.shipment_id}-${index}`}
                      className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                        selectedDriver?.shipment_id === driver.shipment_id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleDriverSelect(driver)}
                    >
                      <div className="flex items-center">
                        <div className="mr-3 relative">
                          <Badge 
                            color="error" 
                            badgeContent={unreadCount}
                            invisible={unreadCount === 0}
                            overlap="circular"
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                          >
                            <Avatar 
                              className={`${getStatusColor(driverStatus)} w-10 h-10`}
                            >
                              {getInitials(driver.driver_name)}
                            </Avatar>
                          </Badge>
                        </div>
                        <div className="flex-grow">
                          <div className="flex justify-between">
                            <span className="font-medium">{driver.driver_name}</span>
                            {isActiveChat && (
                              <span className="text-xs text-gray-500">
                                {formatTime(new Date((driver as ActiveChat).last_activity))}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            {getStatusIcon(driverStatus)}
                            <span className="ml-1 capitalize">{driverStatus}</span>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="text-gray-500">Shipment: </span>
                            <span className="font-medium">{driver.shipment_number}</span>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="text-gray-500">Type: </span>
                            <span className="font-medium">{driver.shipment_type}</span>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="text-gray-500">Direction: </span>
                            <span className="font-medium">{driver.direction}</span>
                          </div>
                          {driver.truck_number && (
                            <div className="text-xs mt-1">
                              <span className="text-gray-500">Truck: </span>
                              <span className="font-medium">{driver.truck_number}</span>
                            </div>
                          )}
                          {driver.trailer_number && (
                            <div className="text-xs mt-1">
                              <span className="text-gray-500">Trailer: </span>
                              <span className="font-medium">
                                {driver.trailer_number}
                                {('carrier_name' in driver && driver.carrier_name) && (
                                  <span className="text-gray-400"> ({driver.carrier_name})</span>
                                )}
                              </span>
                            </div>
                          )}
                          {driver.dock_assignment && (
                            <div className="text-xs mt-1">
                              <span className="text-gray-500">Dock: </span>
                              <span className="font-medium">{driver.dock_assignment}</span>
                            </div>
                          )}
                          {('checkin_time' in driver && driver.checkin_time) && (
                            <div className="text-xs mt-1">
                              <span className="text-gray-500">Checked In: </span>
                              <span className="font-medium">{formatDate(new Date(driver.checkin_time))}</span>
                            </div>
                          )}
                          {('current_location' in driver && driver.current_location) && (
                            <div className="text-xs mt-1">
                              <span className="text-gray-500">Location: </span>
                              <span className="font-medium">{driver.current_location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        
        {/* Chat Panel */}
        <div className="md:col-span-2 lg:col-span-3 bg-white shadow rounded overflow-hidden flex flex-col">
          {selectedDriver ? (
            <>
              {/* Chat Header */}
              <div className="p-3 border-b flex justify-between items-center">
                <div className="flex items-center">
                  <Avatar 
                    className={`${getStatusColor(getDriverStatus(selectedDriver))} mr-3`}
                  >
                    {getInitials(selectedDriver.driver_name)}
                  </Avatar>
                  <div>
                    <div className="font-medium">{selectedDriver.driver_name}</div>
                    <div className="flex items-center text-sm text-gray-500 flex-wrap gap-2">
                      {getStatusIcon(getDriverStatus(selectedDriver))}
                      <span className="ml-1 capitalize">{getDriverStatus(selectedDriver)}</span>
                      
                      {/* Driver Phone */}
                      {selectedDriver.driver_phone && (
                        <Chip 
                          size="small" 
                          label={`Phone: ${selectedDriver.driver_phone}`} 
                          className="ml-2"
                        />
                      )}
                      
                      {/* Truck Number */}
                      {selectedDriver.truck_number && (
                        <Chip 
                          size="small" 
                          label={`Truck: ${selectedDriver.truck_number}`} 
                          className="ml-2"
                        />
                      )}
                      
                      {/* Trailer Number with Carrier SCAC */}
                      {selectedDriver.trailer_number && (
                        <Chip 
                          size="small" 
                          label={`Trailer: ${selectedDriver.trailer_number}${('carrier_name' in selectedDriver && selectedDriver.carrier_name) ? ` (${selectedDriver.carrier_name})` : ''}`} 
                          className="ml-2"
                        />
                      )}
                      
                      {/* Shipment Number */}
                      <Chip 
                        size="small" 
                        label={`Shipment: ${selectedDriver.shipment_number}`} 
                        className="ml-2"
                      />

                      {/* Shipment Type */}
                      {selectedDriver.shipment_type && (
                        <Chip 
                          size="small" 
                          label={`Type: ${selectedDriver.shipment_type}`} 
                          className="ml-2"
                        />
                      )}

                      {/* Direction */}
                      {selectedDriver.direction && (
                        <Chip 
                          size="small" 
                          label={`Direction: ${selectedDriver.direction}`} 
                          className="ml-2"
                        />
                      )}
                      
                      {/* Checkin Time */}
                      {('checkin_time' in selectedDriver && selectedDriver.checkin_time) && (
                        <Chip 
                          size="small" 
                          label={`Checked In: ${formatDate(new Date(selectedDriver.checkin_time))}`} 
                          className="ml-2"
                        />
                      )}
                      
                      {/* Current Location */}
                      {('current_location' in selectedDriver && selectedDriver.current_location) && (
                        <Chip 
                          size="small" 
                          label={`Location: ${selectedDriver.current_location}`} 
                          className="ml-2"
                        />
                      )}
                      
                      {/* Dock Assignment */}
                      {selectedDriver.dock_assignment && (
                        <Chip 
                          size="small" 
                          label={`Dock: ${selectedDriver.dock_assignment}`} 
                          className="ml-2"
                        />
                      )}
                    </div>
                  </div>
                </div>
                
              </div>
              
              {/* Chat Messages */}
              <div className="flex-grow overflow-y-auto p-3 bg-gray-50">
                {loadingMessages ? (
                  <div className="flex justify-center items-center h-full">
                    <CircularProgress size={24} />
                    <Typography variant="body2" className="ml-2">
                      Loading messages...
                    </Typography>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map(message => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.senderRole === 'staff' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 shadow-sm ${
                            message.senderRole === 'staff'
                              ? 'bg-blue-500 text-white'
                              : 'bg-white'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <span className={`font-medium ${
                              message.senderRole === 'staff' ? 'text-blue-100' : 'text-gray-700'
                            }`}>
                              {message.senderName}
                            </span>
                            <span className={`text-xs ml-3 ${
                              message.senderRole === 'staff' ? 'text-blue-100' : 'text-gray-500'
                            }`}>
                              {formatDate(message.timestamp)}
                            </span>
                          </div>
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
              
              {/* Chat Input */}
              <div className="p-3 border-t">
                {canSendMessage(selectedDriver) ? (
                  <div className="flex items-end">
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      variant="outlined"
                      multiline
                      maxRows={4}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small">
                              <AttachFile />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      className="ml-2"
                      disabled={!newMessage.trim() || loadingMessages}
                      onClick={handleSendMessage}
                      sx={{ minWidth: '40px', height: '56px' }}
                    >
                      <Send />
                    </Button>
                  </div>
                ) : (
                  <Paper className="p-3 bg-gray-100">
                    <Typography align="center" color="textSecondary">
                      This driver has checked out and can no longer be contacted.
                    </Typography>
                  </Paper>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col justify-center items-center h-full p-5 text-center text-gray-500">
              <div className="text-xl mb-3">Select a driver to start chatting</div>
              <div className="text-sm">
                Send messages to drivers at any stage of their visit:
                <ul className="mt-2">
                  <li className="flex items-center justify-center">
                    <AccessTime className="text-yellow-500 mr-1" /> Available: Start a new conversation
                  </li>
                  <li className="flex items-center justify-center mt-1">
                    <CheckCircle className="text-green-500 mr-1" /> Active Chats: Continue existing conversations
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DriverComms; 